import type { TaskLifecycleAction, TaskStatus } from '../types/taskQueue.types';

export type TaskTransitionActor = 'any_user' | 'assignee' | 'creator_or_admin' | 'admin';

export interface TaskTransitionDef {
  readonly action: TaskLifecycleAction;
  readonly from: readonly TaskStatus[];
  readonly to: TaskStatus;
  readonly actor: TaskTransitionActor;
}

const TASK_ALL_MUTABLE_STATUSES = [
  'proposed',
  'available',
  'assigned',
  'released',
  'completed',
  'superseded',
] as const satisfies readonly TaskStatus[];

export const TASK_TRANSITIONS = [
  { action: 'approve', from: ['proposed'], to: 'available', actor: 'admin' },
  { action: 'assign', from: ['available', 'released'], to: 'assigned', actor: 'any_user' },
  { action: 'release', from: ['assigned'], to: 'released', actor: 'assignee' },
  { action: 'reassign', from: ['available', 'released', 'assigned'], to: 'assigned', actor: 'admin' },
  { action: 'complete', from: ['assigned'], to: 'completed', actor: 'assignee' },
  { action: 'archive', from: TASK_ALL_MUTABLE_STATUSES, to: 'archived', actor: 'creator_or_admin' },
  // `to` is the fallback; the service restores the pre-archive status when the audit log has it.
  { action: 'reopen', from: ['archived'], to: 'available', actor: 'creator_or_admin' },
] as const satisfies readonly TaskTransitionDef[];

/**
 * Status a reopened task returns to. A task archived while assigned reopens as
 * available so it does not silently land back on its former assignee.
 */
export function resolveReopenStatus(statusBeforeArchive: unknown): TaskStatus {
  switch (statusBeforeArchive) {
    case 'proposed':
    case 'available':
    case 'released':
    case 'completed':
    case 'superseded':
      return statusBeforeArchive;
    default:
      return 'available';
  }
}

export function findTransition(
  action: TaskLifecycleAction,
): TaskTransitionDef | undefined {
  return TASK_TRANSITIONS.find((t) => t.action === action);
}

export function transitionAllowsStatus(
  action: TaskLifecycleAction,
  status: TaskStatus,
): boolean {
  const t = findTransition(action);
  if (!t) return false;
  return (t.from as readonly string[]).includes(status);
}
